// Property Modal Component (Create/Edit) - Updated Version
import { PlusCircle, Edit, Trash2, Search, X, Upload, Save } from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios'
import { Multiselect } from 'multiselect-react-dropdown';
const PropertyModal = ({
  onClose,
  onSave,
  property,
  isCreate,
  thumbNailImage,
  isUploading = false,
  uploadProgress = null
}) => {
  const [formData, setFormData] = useState(() => {
    if (property) {
      return {
        ...property,
        amenityTypes: property.amenities?.map(amenity => amenity?.amenityType) || []
      };
    }

    return {
      address: '',
      city: '',
      state: '',
      unit: '',
      beds: 1,
      baths: 1,
      sqft: 0,
      price: 0,
      lat: null,
      lng: null,
      rentalType: 1,
      available: true,
      availableDt: null,
      minTermMos: 3,
      utilities: false,
      internet: false,
      description: '',
      amenityTypes: [],
      thumbnailImageUrl: '',
      images: [],
      parkingType: 0
    };
  });

  const allAmenities = [
    { id: 1, amenityTypeDisplay: 'No Smoking' },
    { id: 2, amenityTypeDisplay: 'Washer & Dryer' },
    { id: 3, amenityTypeDisplay: 'Dishwasher' },
    { id: 4, amenityTypeDisplay: 'Fenced Yard' },
    { id: 5, amenityTypeDisplay: 'Pet Friendly' },
    { id: 6, amenityTypeDisplay: 'Peloton Available' },
    { id: 7, amenityTypeDisplay: 'Laundry in the building' },
  ];
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [thumbnailPreview, setThumbnailPreview] = useState(thumbNailImage || null);
  const [imageUploadCount, setImageUploadCount] = useState(0);

  const integerFields = ["rentalType"];
  const dateFields = ["availableDt"];

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox'
        ? checked
        : integerFields.includes(name)
          ? Number(value)
          : dateFields.includes(name)
            ? new Date(value).toISOString()
            : value,
    });
  };

  // Sequential image loading for existing property
  const imagesWithURL = async () => {
    if (!property?.images?.length || !property?.id) return;

    const placeholderImages = property.images.map((img, index) => ({
      ...img,
      thumbnail: null,
      index
    }));

    setFormData((prev) => ({
      ...prev,
      images: placeholderImages
    }));

    // Process each image sequentially
    for (let index = 0; index < property.images.length; index++) {
      const img = property.images[index];

      setFormData((prev) => ({
        ...prev,
        images: prev.images.map(currentImg =>
          currentImg.index === index
            ? {
              ...currentImg,
              thumbnail: { previewUrl: `${process.env.REACT_APP_API_URL}${img.imageUrl}` },
              index
            }
            : currentImg
        )
      }));

      await new Promise(resolve => setTimeout(resolve, 50));
    }
  };

  useEffect(() => {
    if (property?.images?.length > 0) {
      imagesWithURL();
    }
  }, [property?.images]);

  const handleThumbnailChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        alert('Thumbnail image must be less than 10MB');
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }

      setThumbnailFile(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);

      setFormData({
        ...formData,
        thumbnailImageUrl: file,
      });
    }
  };

  const handleAdditionalImagesChange = (e) => {
    if (!e.target.files) return;

    const MAX_NEW_IMAGES_PER_UPLOAD = 5;

    // Count new images (those without id) only
    const currentNewImagesCount = formData.images?.filter(img => !img.id)?.length || 0;

    // If already 5 new images exist, don't allow adding more
    if (currentNewImagesCount >= MAX_NEW_IMAGES_PER_UPLOAD) {
      alert(`You can only add ${MAX_NEW_IMAGES_PER_UPLOAD} new images at a time. Please upload them before adding more.`);
      return;
    }

    const incomingFiles = Array.from(e.target.files);

    // Only allow files up to remaining slots (from 5)
    const remainingSlots = MAX_NEW_IMAGES_PER_UPLOAD - currentNewImagesCount;
    const acceptedFiles = incomingFiles.slice(0, remainingSlots);

    // Validate files (must be image and <= 10MB)
    const invalidFiles = acceptedFiles.filter(file => {
      return file.size > 10 * 1024 * 1024 || !file.type.startsWith('image/');
    });

    if (invalidFiles.length > 0) {
      alert('Some files are invalid (must be images under 10MB each)');
      return;
    }

    // Update additionalImages list
    setAdditionalImages(prev => [...prev, ...acceptedFiles]);

    // Prepare preview and metadata
    const readers = acceptedFiles.map(file => {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => {
          resolve({
            thumbnail: {
              previewUrl: reader.result,
              file
            },
            name: file.name,
            isThumbnail: false,
            isNew: true
          });
        };
        reader.readAsDataURL(file);
      });
    });

    // Update formData with new images
    Promise.all(readers).then(newImages => {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages],
      }));

      setImageUploadCount(prev => prev + newImages.length);

      if (incomingFiles.length > acceptedFiles.length) {
        alert(`Only ${acceptedFiles.length} image(s) were accepted. You can only upload 5 new images at a time.`);
      }
    });
  };

  const handleImageDelete = async (id) => {
    if (!id || !property?.id) return; // Can't delete if no ID

    const deleteImgPath = `${process.env.REACT_APP_API_URL}api/Properties/${property.id}/images/${id}`;
    const token = localStorage.getItem("token");

    try {
      const res = await axios.delete(deleteImgPath, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        withCredentials: true,
        timeout: 30000
      });
      return res.data;
    } catch (error) {
      console.error("Error deleting image:", error.response?.data || error.message);
      if (error.response?.status === 401) {
        alert("Unauthorized: Please log in again.");
      } else {
        alert("Failed to delete image from server");
      }
      throw error;
    }
  };

  const deleteThumbNail = async () => {
    const thumbNail = formData.images.find((img) => img.isThumbnail === true);
    if (thumbNail && thumbNail.id) {
      await removeImage(thumbNail.id, thumbNail.index);
    }
  };

  const removeImage = async (imgId, index) => {
    setIsDeleting(true);
    const imageName = `Image ${index + 1}`;
    // Show loading state for this specific image
    setDeleteProgress(`Deleting ${imageName}...`);
    // If it's an existing image (has ID), delete from server
    if (imgId) {
      try {
        await handleImageDelete(imgId);
        await new Promise(resolve => setTimeout(resolve, 800)); // Add delay for visual feedback
        setDeleteProgress(`${imageName} deleted successfully`);
      } catch (error) {
        console.error("Failed to delete image from server:", error);
        setDeleteProgress(`Failed to delete ${imageName}`);
        setTimeout(() => {
          setDeleteProgress(null);
          setIsDeleting(false); // ✅ moved inside
        }, 2000);
        return; // Don't remove from UI if server delete failed
      }
    }

    // Remove from local state
    const updatedImages = formData.images.filter(img =>
      imgId ? img.id !== imgId : img !== imgId
    );

    setFormData({
      ...formData,
      images: updatedImages,
    });
    // Clear progress after 2 seconds
    setTimeout(() => {
      setDeleteProgress(null);
      setIsDeleting(false); // ✅ moved here as well
    }, 2000);
  };

  // Remove image by reference (for new images without ID)
  const removeNewImage = (imageRef) => {
    const updatedImages = formData.images.filter(img => img !== imageRef);
    setFormData({
      ...formData,
      images: updatedImages,
    });
  };

  useEffect(() => {
    if (property?.id) {
      setFormData((prev) => ({
        ...prev,
        thumbnailImageUrl: thumbNailImage
      }));
    }
  }, [property]);

  const handleSubmit = (e) => {
    if (e) e.preventDefault();

    // Validation checks
    if (!formData.address || !formData.city || !formData.state || !formData.unit ||
      !formData.price || !formData.thumbnailImageUrl || !formData.lat || !formData.lng || !formData.minTermMos
    ) {
      alert('Please fill out all required fields');
      return;
    }

    if (!(formData.minTermMos >= 1 && formData.minTermMos <= 24)) {
      alert('Please fill out minimum term months between 1 and 24');
      return;
    }

    // Format the data to ensure proper types
    const formattedData = {
      ...formData,
      lat: formData.lat ? parseFloat(formData.lat) : null,
      lng: formData.lng ? parseFloat(formData.lng) : null,
      price: parseFloat(formData.price),
      beds: formData.beds !== null && formData.beds !== undefined && formData.beds !== ''
        ? parseInt(formData.beds) : 0,
      baths: formData.baths !== null && formData.baths !== undefined && formData.baths !== ''
        ? parseInt(formData.baths) : 0,
      sqft: formData.sqft !== null && formData.sqft !== undefined && formData.sqft !== ''
        ? parseInt(formData.sqft) : 0,
      minTermMos: parseInt(formData.minTermMos),
      parkingType: parseInt(formData.parkingType) || 0
    };

    // Pass the formatted data to parent component
    onSave(formattedData);
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }} tabIndex="-1">
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title fw-bold">
              {isCreate ? 'Create New Property' : 'Edit Property'}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              disabled={isUploading}
            ></button>
          </div>

          <div className="modal-body">
            {/* Upload Progress Indicator */}
            {isUploading && (
              <div className="alert alert-info d-flex align-items-center mb-4">
                <div className="spinner-border spinner-border-sm me-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div>
                  <strong>Uploading...</strong>
                  {uploadProgress && <div className="small">{uploadProgress}</div>}
                </div>
              </div>
            )}

            {/* Delete Progress Indicator */}
            {deleteProgress && (
              <div className="alert alert-warning d-flex align-items-center mb-4">
                <div className="spinner-border spinner-border-sm me-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div>
                  <strong>{deleteProgress}</strong>
                </div>
              </div>
            )}

            <div className="row g-4">
              {/* Left Column - Basic Information */}
              <div className="col-md-6">
                <h5 className="fw-medium text-dark border-bottom pb-2 mb-3">Basic Information</h5>

                <div className="mb-3">
                  <label className="form-label">Address*</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="form-control"
                    placeholder="e.g. 172 River Street"
                    disabled={isUploading}
                  />
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">City*</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="e.g. Troy"
                      disabled={isUploading}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">State*</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="e.g. NY"
                      disabled={isUploading}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Unit*</label>
                    <input
                      type="text"
                      name="unit"
                      value={formData.unit}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="e.g. 403"
                      disabled={isUploading}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Price ($)*</label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      min="0"
                      className="form-control"
                      placeholder="e.g. 2300"
                      disabled={isUploading}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Latitude*</label>
                    <input
                      type="number"
                      name="lat"
                      value={formData.lat || ''}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (['e', 'E'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      step="0.000001"
                      placeholder="e.g. 42.729717"
                      className="form-control"
                      disabled={isUploading}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Longitude*</label>
                    <input
                      type="number"
                      name="lng"
                      value={formData.lng || ''}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (['e', 'E'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      step="0.000001"
                      placeholder="e.g. -73.694157"
                      className="form-control"
                      disabled={isUploading}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Beds</label>
                    <input
                      type="number"
                      name="beds"
                      value={formData.beds}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      min="0"
                      className="form-control"
                      disabled={isUploading}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Baths</label>
                    <input
                      type="number"
                      name="baths"
                      value={formData.baths}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      min="0"
                      className="form-control"
                      disabled={isUploading}
                    />
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Sqft</label>
                    <input
                      type="number"
                      name="sqft"
                      value={formData.sqft}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      min="0"
                      className="form-control"
                      disabled={isUploading}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Rental Type*</label>
                    <select
                      name="rentalType"
                      value={formData.rentalType}
                      onChange={handleChange}
                      className="form-select"
                      disabled={isUploading}
                    >
                      <option value="0">Unfurnished</option>
                      <option value="1">Furnished</option>
                      <option value="2">Semi-Furnished</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Min Term (Months)*</label>
                    <input
                      type="number"
                      name="minTermMos"
                      value={formData.minTermMos}
                      onChange={handleChange}
                      onKeyDown={(e) => {
                        if (['e', 'E', '+', '-', '.'].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      min="1"
                      className="form-control"
                      disabled={isUploading}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Parking Type</label>
                    <select
                      name="parkingType"
                      value={formData.parkingType}
                      onChange={handleChange}
                      className="form-select"
                      disabled={isUploading}
                    >
                      <option value="0">No Parking</option>
                      <option value="1">On-Street Parking</option>
                      <option value="2">Reserved Spot</option>
                    </select>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Available Date</label>
                  <input
                    type="date"
                    name="availableDt"
                    value={(formData.availableDt &&
                      formData.availableDt !== "0001-01-01T00:00:00") ? formData.availableDt.split('T')[0] : ''}
                    onChange={handleChange}
                    className="form-control"
                    disabled={isUploading}
                  />
                </div>

                <div className="mb-3 d-flex gap-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="available"
                      name="available"
                      checked={formData.available}
                      onChange={handleChange}
                      disabled={isUploading}
                    />
                    <label className="form-check-label" htmlFor="available">
                      Available
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="utilities"
                      name="utilities"
                      checked={formData.utilities}
                      onChange={handleChange}
                      disabled={isUploading}
                    />
                    <label className="form-check-label" htmlFor="utilities">
                      Utilities Included
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="internet"
                      name="internet"
                      checked={formData.internet}
                      onChange={handleChange}
                      disabled={isUploading}
                    />
                    <label className="form-check-label" htmlFor="internet">
                      Internet Included
                    </label>
                  </div>
                </div>
              </div>

              {/* Right Column - Details & Media */}
              <div className="col-md-6">
                <h5 className="fw-medium text-dark border-bottom pb-2 mb-3">Details & Media</h5>

                <div className="mb-3">
                  <label className="form-label">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="4"
                    className="form-control"
                    placeholder="Describe the property..."
                    disabled={isUploading}
                  ></textarea>
                </div>

                <div className="mb-3">
                  <label className="form-label">Features</label>
                  <Multiselect
                    options={allAmenities}
                    selectedValues={allAmenities.filter(option =>
                      formData.amenityTypes?.includes(option.id)
                    )}
                    onSelect={(selectedList) => {
                      setFormData({
                        ...formData,
                        amenityTypes: selectedList.map(item => item.id)
                      });
                    }}
                    onRemove={(selectedList) => {
                      setFormData({
                        ...formData,
                        amenityTypes: selectedList.map(item => item.id)
                      });
                    }}
                    displayValue="amenityTypeDisplay"
                    showCheckbox={true}
                    placeholder={formData.amenityTypes?.length > 0 ? '' : 'Select Features'}
                    style={{
                      chips: {
                        background: '#33C5FF',  // ⬅️ Your button's background color
                        color: 'white',
                        borderRadius: '20px',   // Optional: makes chips pill-shaped like your button
                        padding: '4px 10px'     // Optional: improves spacing
                      },
                      searchBox: {
                        border: '1px solid #dee2e6',
                        borderRadius: '0.375rem',
                        minHeight: '0px',
                        padding: '8px'
                      },
                      optionContainer: {
                        border: '1px solid #dee2e6'
                      },
                      option: {
                        color: '#212529',
                        backgroundColor: 'white'
                      },
                      highlightOption: {
                        backgroundColor: '#33C5FF',
                        color: 'white'
                      },
                      checkbox: {
                        accentColor: '#33C5FF'
                      }
                    }}

                    disable={isUploading}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Thumbnail Image*</label>
                  <div className="mt-1">
                    <div className="border border-2 border-dashed rounded p-3 text-center">
                      {thumbnailPreview ? (
                        <div className="position-relative" style={{ height: "200px" }}>
                          <img
                            src={thumbnailPreview}
                            alt="Thumbnail preview"
                            className="img-fluid h-100 object-fit-cover rounded"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setThumbnailPreview(null);
                              setThumbnailFile(null);
                              deleteThumbNail();
                              setFormData({
                                ...formData,
                                thumbnailImageUrl: '',
                              });
                            }}
                            className="position-absolute top-0 end-0 btn btn-sm btn-light rounded-circle m-2"
                            disabled={isUploading}
                          >
                            <X size={16} className="text-danger" />
                          </button>
                        </div>
                      ) : (
                        <label className="d-flex flex-column align-items-center p-4 cursor-pointer">
                          <Upload size={40} className="text-secondary mb-2" />
                          <span className="text-secondary">
                            Click to upload thumbnail
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleThumbnailChange}
                            className="d-none"
                            disabled={isUploading}
                          />
                        </label>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">
                    Additional Images
                    <span className="text-secondary ms-2">
                      ({formData?.images?.filter(img => !img.isThumbnail)?.length || 0}/{isCreate ? '5' : '15'} max)
                    </span>
                  </label>
                  <div>
                    <div className="border border-2 border-dashed rounded p-3">
                      <div className="row row-cols-3 g-2 mb-3">
                        {formData?.images?.filter(image => image.isThumbnail !== true).map((image, index) => (
                          <div key={image.id || index} className="col">
                            <div className="position-relative" style={{ height: "80px" }}>
                              <img
                                src={image?.thumbnail?.previewUrl}
                                alt={`Property image ${index + 1}`}
                                className="img-fluid h-100 object-fit-cover rounded"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (image.id) {
                                    removeImage(image.id, index);
                                  } else {
                                    removeNewImage(image);
                                  }
                                }}
                                className="position-absolute top-0 end-0 btn btn-sm btn-light rounded-circle m-1"
                                disabled={isUploading || isDeleting}
                              >
                                <X size={12} className="text-danger" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      {
                       
                        (isCreate ?

                          (!formData.images || formData.images.filter(img => !img.isThumbnail)?.length < 5) 
                          :
                          (!formData.images || formData.images.filter(img => !img.isThumbnail)?.length < 15)
                        ) && 
                        (
                          <div className="border border-1 rounded p-3 text-center">
                            <label className="d-flex flex-column align-items-center cursor-pointer">
                              <Upload size={24} className="text-secondary mb-2" />
                              <span className="text-secondary small">
                                {isCreate ? 'Add up to 5 images initially' : 'Add up to 15 images total'}
                              </span>
                              <input
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={handleAdditionalImagesChange}
                                className="d-none"
                                disabled={isUploading}
                              />
                            </label>
                          </div>
                        )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="btn btn-primary d-flex align-items-center"
              disabled={isUploading}
            >
              {isUploading ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  {isCreate ? 'Creating...' : 'Saving...'}
                </>
              ) : (
                <>
                  <Save size={18} className="me-2" />
                  {isCreate ? 'Create Property' : 'Save Changes'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyModal;